import {useState, useEffect} from 'react'
import type React from 'react'
import {clsx} from 'clsx'

import {Hero as PrimerHero} from '@primer/react-brand'
import type {PrimerComponentHero} from '@github-ui/swp-core/schemas/contentful/contentTypes/primerComponentHero'
import {ContentfulSubnav} from '@github-ui/swp-core/components/contentful/ContentfulSubnav'
import type {PrimerComponentSubnav} from '@github-ui/swp-core/schemas/contentful/contentTypes/primerComponentSubnav'
import type {PrimerComponentPricingOptions} from '@github-ui/swp-core/schemas/contentful/contentTypes/primerComponentPricingOptions'
import {documentToPlainTextString} from '@github-ui/swp-core/lib/utils/analytics'

import {isFeatureEnabled} from '@github-ui/feature-flags'
import {Hero as PrimerHeroNext} from '@primer/react-brand-next'

import PlanCard from '../../../../../../brand/components/PlanCard/PlanCard'
import type {PlanCardProps} from '../../../../../../brand/components/PlanCard/PlanCard'

import ImageBgDesktop from './../../../assets/images/background-desktop.webp'
import ImageBgMobile from './../../../assets/images/background-mobile.webp'

import Styles from './Hero.module.css'

export interface HeroProps {
  pricingOptions: PrimerComponentPricingOptions
  contentfulContent: PrimerComponentHero
  subnav?: PrimerComponentSubnav
}

const defaultProps: Partial<HeroProps> = {}

const Hero: React.FC<HeroProps> = props => {
  const isPrimerBrandNextEnabled = isFeatureEnabled('primer_brand_next')
  const initializedProps = {...defaultProps, ...props}

  const {pricingOptions, contentfulContent, subnav} = initializedProps
  const {heading, description, descriptionVariant} = contentfulContent.fields || {}

  const [isMounted, setIsMounted] = useState<boolean>(false)

  // Transform Contentful pricing options to PlanCard format
  const plans: PlanCardProps[] =
    pricingOptions.fields.items?.map(item => ({
      id: item.sys.id,
      label: item.fields.label?.fields.text || '',
      title: item.fields.heading ? documentToPlainTextString(item.fields.heading) : '',
      description: item.fields.description ? documentToPlainTextString(item.fields.description) : '',
      price: {
        value: item.fields.currentPrice ? parseInt(documentToPlainTextString(item.fields.currentPrice), 10) : 0,
        currency: item.fields.currencyCode || 'USD',
        currencySymbol: item.fields.currencySymbol || '$',
        detailText: item.fields.priceTrailingText || '',
      },
      cta1: {
        label: item.fields.callToActionPrimary?.fields.text || '',
        href: item.fields.callToActionPrimary?.fields.href || '',
      },
      cta2: {
        label: item.fields.callToActionSecondary?.fields.text || '',
        href: item.fields.callToActionSecondary?.fields.href || '',
      },
      detailText: item.fields.footnote || '',
    })) || []

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIsMounted(true)
  }, [])

  return (
    <section id="hero" className={Styles['Hero']}>
      <div
        className={clsx(Styles['Hero__background'], !isMounted && Styles['Hero__background--isHidden'])}
        style={{
          ['--backgroundUrl-desktop']: `url("${ImageBgDesktop}")`,
          ['--backgroundUrl-mobile']: `url("${ImageBgMobile}")`,
        }}
      />

      <div className={Styles['Hero__container']}>
        {subnav ? <ContentfulSubnav component={subnav} linkVariant="default" /> : null}

        <div className={Styles['Hero__content']}>
          {isPrimerBrandNextEnabled ? (
            <PrimerHeroNext align="center" className={Styles['Hero__heroBlock']} data-hpc>
              <PrimerHeroNext.Heading>{heading}</PrimerHeroNext.Heading>
              <PrimerHeroNext.Description variant={descriptionVariant}>{description}</PrimerHeroNext.Description>
            </PrimerHeroNext>
          ) : (
            <PrimerHero align="center" className={Styles['Hero__heroBlock']} data-hpc>
              <PrimerHero.Heading className={Styles['Hero__title']}>{heading}</PrimerHero.Heading>
              <PrimerHero.Description variant={descriptionVariant}>{description}</PrimerHero.Description>
            </PrimerHero>
          )}

          <div className={Styles['PricingPlans']}>
            {plans.map(plan => (
              <PlanCard key={plan.id} {...plan} />
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}

export default Hero
